import { notify, TYPE } from "@8x8/oxygen-toaster";
import { DURATION } from "@/react/features/support-tools/dialplans/constants";
import { errorNotification } from "@/react/notifications";
import downloadBlob from "@/react/downloadBlob";
import { useMemo } from "react";

const UNEXPECTED_ERROR_MESSAGE = Object.freeze(
  "Unexpected error. Please contact TAP Team"
);

const useDialplansApi = ({
  createDialplanApi,
  updateDialplanApi,
  deleteDialplanApi,
  downloadXmlFile,
  uploadXmlFile,
  dialplan,
  setDialplan,
  setIsModalOpenFor,
  setIsActionTriggered,
  setIsUploadingFile,
}) => {
  const handleCreate = (createdDialplan) => {
    createDialplanApi({ dialplan: createdDialplan })
      .then((response) => {
        if (response.data?.operationResult?.success) {
          notify({
            content: `Dial Plan ${createdDialplan.name} created successfully`,
            type: TYPE.success,
            duration: DURATION,
          });
        } else {
          notify({
            content: `Create ${createdDialplan.name}: ${
              response.data?.operationResult?.failure?.message ||
              UNEXPECTED_ERROR_MESSAGE
            }`,
            type: TYPE.error,
            duration: DURATION,
          });
        }
        setIsModalOpenFor(null);
      })
      .catch((error) => {
        errorNotification(error);
      })
      .finally(() => {
        setIsActionTriggered(false);
      });
  };

  const handleUpdate = (updatedDialplan) => {
    updateDialplanApi({ dialplan: updatedDialplan })
      .then((response) => {
        if (response.data?.operationResult?.success) {
          // Because the way of how Smp works, when trying to update a dial plan with a different name, it is going to
          // create a new one instead of updating (since name serves as ID)
          notify({
            content: `Dial Plan ${updatedDialplan.name} ${
              updatedDialplan.name === updatedDialplan.id
                ? "updated"
                : "created"
            } successfully`,
            type: TYPE.success,
            duration: DURATION,
          });
        } else {
          notify({
            content: `${
              updatedDialplan.name === updatedDialplan.id ? "Update" : "Create"
            } ${updatedDialplan.name}: ${
              response.data?.operationResult?.failure?.message ||
              UNEXPECTED_ERROR_MESSAGE
            }`,
            type: TYPE.error,
            duration: DURATION,
          });
        }
        setIsModalOpenFor(null);
      })
      .catch((error) => {
        errorNotification(error);
      })
      .finally(() => {
        setIsActionTriggered(false);
      });
  };

  const handleDelete = () => {
    deleteDialplanApi({ dialplanId: dialplan.id })
      .then((response) => {
        if (!response.data) {
          notify({
            content: `Dial Plan ${dialplan.name} deleted successfully`,
            type: TYPE.success,
            duration: DURATION,
          });
        } else {
          notify({
            content: `Delete ${dialplan.name}: ${
              response.data?.operationResult?.failure?.message ||
              UNEXPECTED_ERROR_MESSAGE
            }`,
            type: TYPE.error,
            duration: DURATION,
          });
        }
      })
      .catch((error) => {
        errorNotification(error);
      });
  };

  const handleDownloadXML = () => {
    downloadXmlFile({ dialplan })
      .then((response) => {
        if (response.data?.operationResult?.success) {
          const blob = new Blob([response.data.operationResult.success], {
            type: "application/xml",
          });
          downloadBlob({
            blob,
            filename: `${dialplan.name || "dialplan"}.xml`,
          });
        } else {
          notify({
            content: UNEXPECTED_ERROR_MESSAGE,
            type: TYPE.error,
            duration: DURATION,
          });
        }
      })
      .catch((error) => {
        errorNotification(error);
      });
  };

  const handleImportXml = (files) => {
    uploadXmlFile({ file: files[0] })
      .then((response) => {
        if (response.data?.operationResult?.success) {
          notify({
            content: `Successful XML import`,
            type: TYPE.success,
            duration: DURATION,
          });
          setDialplan(response.data.operationResult.success);
        } else {
          notify({
            content: `XML import: ${response.data?.operationResult?.failure.message}`,
            type: TYPE.error,
            duration: DURATION,
          });
        }
      })
      .catch((error) => {
        errorNotification(error);
      })
      .finally(() => setIsUploadingFile(false));
  };

  return useMemo(
    () => ({
      handleCreate,
      handleUpdate,
      handleDelete,
      handleImportXml,
      handleDownloadXML,
    }),
    [dialplan]
  );
};

export default useDialplansApi;
